/**
 * @fileoverview API token validation schemas
 *
 * Schemas for managing user API tokens used for external integrations.
 * Tokens provide scoped access to the arml.ink API.
 *
 * @module shared/schemas/tokens
 */

import { z } from 'zod';

// =============================================================================
// Constants
// =============================================================================

/** Maximum token name length */
export const MAX_TOKEN_NAME_LENGTH = 100;

/** Available API scopes */
export const API_SCOPES = ['links:create', 'links:read', 'links:delete'] as const;

/** Type for API scope */
export type ApiScope = (typeof API_SCOPES)[number];

/** Default scopes for new tokens */
export const DEFAULT_SCOPES: ApiScope[] = ['links:create', 'links:read'];

// =============================================================================
// Token Schemas
// =============================================================================

/**
 * Schema for API scope validation.
 */
export const scopeSchema = z.enum(API_SCOPES);

/**
 * Schema for creating a new API token.
 *
 * @example
 * ```typescript
 * const input = createTokenSchema.parse({
 *   name: 'My Integration',
 *   scopes: ['links:create', 'links:read'],
 *   expiresAt: '2025-12-31T23:59:59Z',
 * });
 * ```
 */
export const createTokenSchema = z.object({
  name: z
    .string()
    .min(1, 'Token name is required')
    .max(MAX_TOKEN_NAME_LENGTH, `Name must be ${MAX_TOKEN_NAME_LENGTH} characters or less`)
    .trim(),
  expiresAt: z.string().datetime().optional().nullable(),
  scopes: z.array(scopeSchema).min(1, 'At least one scope is required').default(DEFAULT_SCOPES),
});

/** TypeScript type for create token input */
export type CreateTokenInput = z.infer<typeof createTokenSchema>;

/**
 * Response schema for token list (safe, no secrets).
 */
export const tokenResponseSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  tokenPrefix: z.string(),
  scopes: z.array(scopeSchema),
  lastUsedAt: z.string().datetime().nullable(),
  expiresAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
});

/** TypeScript type for token response */
export type TokenResponse = z.infer<typeof tokenResponseSchema>;

/**
 * Response schema for newly created token (includes the actual token value).
 * This is the only time the full token is returned.
 */
export const createdTokenResponseSchema = tokenResponseSchema.extend({
  tokenValue: z.string(),
});

/** TypeScript type for created token response */
export type CreatedTokenResponse = z.infer<typeof createdTokenResponseSchema>;
